// Karta zaměstnance kompletní (F10 10.1; kontrakt tag Zamestnanci, akce 08 §3.13):
//   GET/POST /zamestnanci/{id}/lhuty, PUT/DELETE …/{podId}        lhůty se stavem, který spočítal server
//   GET/POST /zamestnanci/{id}/dokumenty | kvalifikace | oopp | sverene-veci | hodnoceni | urazy (+ PUT/DELETE)
//   POST …/oopp/sada, …/oopp/{podId}/podepsat-prevzeti, …/sverene-veci/{podId}/podepsat-prevzeti | vratit
//   POST …/urazy/{podId}/potvrdit; GET /urazy (kniha napříč firmou)
//   GET /zamestnanci/{id}/checklist, PUT …/checklist (jedna položka); GET/PUT …/benefity
//   GET /ja/karta                                                vlastní lhůty, OOPP, svěřené věci a `k_podpisu`
// Stavy lhůt, obnovu OOPP i „podepsáno“ **počítá server** — UI je jen zobrazuje (CLAUDE.md).
import type { Schema } from '@ecoser/shared';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { api } from './klient';
import { KLIC_ZAMESTNANCI } from './zamestnanci';

export type ZamLhuta = Schema<'ZamLhuta'>;
export type ZamDokument = Schema<'ZamDokument'> & { id: string };
export type ZamKvalifikace = Schema<'ZamKvalifikace'> & { id: string };
export type ZamOopp = Schema<'ZamOopp'> & { id: string };
export type ZamSverenaVec = Schema<'ZamSverenaVec'> & { id: string };
export type ZamHodnoceni = Schema<'ZamHodnoceni'> & { id: string };
export type ZamUraz = Schema<'ZamUraz'> & { id: string };
export type ZamChecklistPolozka = Schema<'ZamChecklistPolozka'>;
export type ZamChecklistPrehled = Schema<'ZamChecklistPrehled'>;
export type ZamBenefit = Schema<'ZamBenefit'>;
export type JaKarta = Schema<'JaKarta'> & { dokumenty?: ZamDokument[] };
export type JaKartaKPodpisu = Schema<'JaKartaKPodpisu'>;
export type PodpisVstupTablet = Schema<'PodpisVstupTablet'>;
export type StavLhuty = ZamLhuta['stav'];

interface Seznam<T> {
  polozky: T[];
  celkem: number;
}

export const AKCE_ZAKLADNI = 'zamestnanec.cist_zakladni';
export const AKCE_CIST = 'zamestnanec.cist';
/**
 * Karta bez peněz (role Fakturace — rozhodnutí zadavatele 3. 11. 2026): záložky jako celá karta, ale server vynechá číslo
 * účtu, mzdu, poznámku, benefity, mzdový výměr a soubory smluv; zápis nedává.
 */
export const AKCE_BEZ_PENEZ = 'zamestnanec.cist_bez_penez';
export const AKCE_UCET = 'zamestnanec.ucet';
export const AKCE_VLASTNI = 'zamestnanec.vlastni_data';
export const AKCE_URAZ_ZAPSAT = 'uraz.zapsat';
export const AKCE_URAZ_POTVRDIT = 'uraz.potvrdit';

/** Popisky typů dokumentů (prototyp `ZAM_DOK_TYPY`). */
export const TYPY_DOKUMENTU: ReadonlyArray<{ klic: ZamDokument['typ']; popisek: string }> = [
  { klic: 'smlouva', popisek: 'Pracovní smlouva' },
  { klic: 'dodatek', popisek: 'Dodatek' },
  { klic: 'mzdovy_vymer', popisek: 'Mzdový výměr' },
  { klic: 'hmotna', popisek: 'Hmotná odpovědnost' },
  { klic: 'gdpr', popisek: 'GDPR souhlas' },
  { klic: 'prohlidka', popisek: 'Lékařský posudek' },
  { klic: 'skoleni', popisek: 'Osvědčení / školení' },
  { klic: 'prukaz', popisek: 'Průkaz' },
  { klic: 'jine', popisek: 'Jiné' },
];
export const POPIS_TYPU_HODNOCENI: Readonly<Record<ZamHodnoceni['typ'], string>> = {
  pohovor: 'Pohovor',
  pochvala: 'Pochvala',
  upozorneni: 'Kázeňské upozornění',
};
export const POPIS_ZAVAZNOSTI_URAZU: Readonly<Record<ZamUraz['zavaznost'], string>> = {
  drobny: 'Drobný (bez neschopenky)',
  neschopenka: 'S pracovní neschopností',
  zavazny: 'Závažný (hospitalizace nad 5 dní)',
  smrtelny: 'Smrtelný',
};
/** Presety svěřených věcí (prototyp H:36960). */
export const PRESETY_SVERENYCH_VECI = [
  'Mobilní telefon',
  'Tankovací karta',
  'Klíče / čip',
  'Notebook',
  'Vozidlo',
] as const;

/** Štítek stavu lhůty → třída `stitek--…` (prototyp `zamStavBadge`). */
export function tridaStavuLhuty(stav: StavLhuty): string {
  switch (stav) {
    case 'ok':
      return 'stitek stitek--ok';
    case 'brzy':
    case 'blizi':
      return 'stitek stitek--warn';
    case 'propadlo':
      return 'stitek stitek--danger';
    default:
      return 'stitek stitek--neutral';
  }
}
export function tridaStavuObnovy(stav: ZamOopp['stav_obnovy']): string {
  if (stav === 'ok') return 'stitek stitek--ok';
  if (stav === 'brzy') return 'stitek stitek--warn';
  if (stav === 'propadlo') return 'stitek stitek--danger';
  return 'stitek stitek--neutral';
}

export const klicKarty = (id: string | undefined, cast: string) =>
  [...KLIC_ZAMESTNANCI, 'karta', id ?? '', cast] as const;
export const KLIC_JA_KARTA = ['ja', 'karta'] as const;

function cesta(id: string, cast: string): string {
  return `/zamestnanci/${encodeURIComponent(id)}/${cast}`;
}

async function seznam<T>(url: string, signal?: AbortSignal): Promise<T[]> {
  const o = await api.get<Seznam<T>>(`${url}?velikost=200`, { signal });
  return o.polozky;
}

/** Hook na podřízenou kolekci karty (lhůty, OOPP…); `povoleno = false` = záložku uživatel nesmí — nevolá se. */
function useKolekce<T>(id: string | undefined, cast: string, povoleno = true) {
  return useQuery({
    queryKey: klicKarty(id, cast),
    queryFn: ({ signal }) => seznam<T>(cesta(id as string, cast), signal),
    enabled: Boolean(id) && povoleno,
  });
}

export const useLhuty = (id: string | undefined, povoleno = true) =>
  useKolekce<ZamLhuta>(id, 'lhuty', povoleno);
export const useDokumenty = (id: string | undefined, povoleno = true) =>
  useKolekce<ZamDokument>(id, 'dokumenty', povoleno);
export const useKvalifikace = (id: string | undefined, povoleno = true) =>
  useKolekce<ZamKvalifikace>(id, 'kvalifikace', povoleno);
export const useOopp = (id: string | undefined, povoleno = true) =>
  useKolekce<ZamOopp>(id, 'oopp', povoleno);
export const useSvereneVeci = (id: string | undefined, povoleno = true) =>
  useKolekce<ZamSverenaVec>(id, 'sverene-veci', povoleno);
export const useHodnoceni = (id: string | undefined, povoleno = true) =>
  useKolekce<ZamHodnoceni>(id, 'hodnoceni', povoleno);
export const useUrazy = (id: string | undefined, povoleno = true) =>
  useKolekce<ZamUraz>(id, 'urazy', povoleno);

export function useChecklist(id: string | undefined, povoleno = true) {
  return useQuery({
    queryKey: klicKarty(id, 'checklist'),
    queryFn: ({ signal }) =>
      api.get<ZamChecklistPrehled>(cesta(id as string, 'checklist'), { signal }),
    enabled: Boolean(id) && povoleno,
  });
}
export function useBenefity(id: string | undefined, povoleno = true) {
  return useQuery({
    queryKey: klicKarty(id, 'benefity'),
    queryFn: ({ signal }) => api.get<ZamBenefit>(cesta(id as string, 'benefity'), { signal }),
    enabled: Boolean(id) && povoleno,
  });
}
export function useJaKarta(povoleno = true) {
  return useQuery({
    queryKey: KLIC_JA_KARTA,
    queryFn: ({ signal }) => api.get<JaKarta>('/ja/karta', { signal }),
    enabled: povoleno,
  });
}

/** Po zápisu: kolekce karty i vlastní karta (podpis převzetí mění obojí). */
export function obnovitKartu(queryClient: QueryClient, id: string, cast?: string) {
  const sliby = [
    queryClient.invalidateQueries({
      queryKey: cast ? klicKarty(id, cast) : [...KLIC_ZAMESTNANCI, 'karta', id],
    }),
    queryClient.invalidateQueries({ queryKey: KLIC_JA_KARTA }),
    queryClient.invalidateQueries({ queryKey: ['ja', 'notifikace'] }),
  ];
  return Promise.all(sliby);
}

// --- zápisy (těla podle kontraktu; `?nahled=true` řeší asistent) ------------------------------------------------
/** Tělo lhůty: `null` smaže hodnotu (platnost, poznámka), `aktivni: null` = podle číselníku. */
export interface LhutaVstup {
  kod: string;
  aktivni?: boolean | null;
  platnost_do?: string | null;
  posledni?: string | null;
  poznamka?: string | null;
  zam_dokument_id?: string | null;
}
export const zalozitLhutu = (id: string, telo: LhutaVstup) =>
  api.post<ZamLhuta>(cesta(id, 'lhuty'), telo);
export const upravitLhutu = (id: string, podId: string, telo: Partial<LhutaVstup>) =>
  api.put<ZamLhuta>(`${cesta(id, 'lhuty')}/${podId}`, telo);
export const smazatLhutu = (id: string, podId: string) =>
  api.delete<void>(`${cesta(id, 'lhuty')}/${podId}`);

export type DokumentVstup = Schema<'ZamDokumentVstup'>;
export const zalozitDokument = (id: string, telo: DokumentVstup) =>
  api.post<ZamDokument>(cesta(id, 'dokumenty'), telo);
export const upravitDokument = (id: string, podId: string, telo: DokumentVstup) =>
  api.put<ZamDokument>(`${cesta(id, 'dokumenty')}/${podId}`, telo);
export const smazatDokument = (id: string, podId: string) =>
  api.delete<void>(`${cesta(id, 'dokumenty')}/${podId}`);

export type KvalifikaceVstup = Schema<'ZamKvalifikaceVstup'>;
export const zalozitKvalifikaci = (id: string, telo: KvalifikaceVstup) =>
  api.post<ZamKvalifikace>(cesta(id, 'kvalifikace'), telo);
export const upravitKvalifikaci = (id: string, podId: string, telo: KvalifikaceVstup) =>
  api.put<ZamKvalifikace>(`${cesta(id, 'kvalifikace')}/${podId}`, telo);
export const smazatKvalifikaci = (id: string, podId: string) =>
  api.delete<void>(`${cesta(id, 'kvalifikace')}/${podId}`);

export type OoppVstup = Schema<'ZamOoppVstup'>;
export const vydatOopp = (id: string, telo: OoppVstup) =>
  api.post<ZamOopp>(cesta(id, 'oopp'), telo);
export const vydatSaduOopp = (id: string, sada?: 'ridic' | 'sklad') =>
  api.post<ZamOopp[]>(`${cesta(id, 'oopp')}/sada`, sada ? { sada } : {});
export const upravitOopp = (id: string, podId: string, telo: OoppVstup) =>
  api.put<ZamOopp>(`${cesta(id, 'oopp')}/${podId}`, telo);
export const smazatOopp = (id: string, podId: string) =>
  api.delete<void>(`${cesta(id, 'oopp')}/${podId}`);
export const podepsatPrevzetiOopp = (id: string, podId: string, telo: PodpisVstupTablet) =>
  api.post<ZamOopp>(`${cesta(id, 'oopp')}/${podId}/podepsat-prevzeti`, telo);

export type SverenaVecVstup = Schema<'ZamSverenaVecVstup'>;
export const predatVec = (id: string, telo: SverenaVecVstup) =>
  api.post<ZamSverenaVec>(cesta(id, 'sverene-veci'), telo);
export const upravitVec = (id: string, podId: string, telo: SverenaVecVstup) =>
  api.put<ZamSverenaVec>(`${cesta(id, 'sverene-veci')}/${podId}`, telo);
export const smazatVec = (id: string, podId: string) =>
  api.delete<void>(`${cesta(id, 'sverene-veci')}/${podId}`);
export const vratitVec = (id: string, podId: string, vraceno?: string) =>
  api.post<ZamSverenaVec>(
    `${cesta(id, 'sverene-veci')}/${podId}/vratit`,
    vraceno ? { vraceno } : {},
  );
export const podepsatPrevzetiVeci = (id: string, podId: string, telo: PodpisVstupTablet) =>
  api.post<ZamSverenaVec>(`${cesta(id, 'sverene-veci')}/${podId}/podepsat-prevzeti`, telo);

/**
 * „Podepsáno na papíře“ (rozhodnutí zadavatele 27. 9. 2026, 05/236): sken výdejního listu se nahraje předem
 * jako příloha záznamu (`zam_oopp` / `zam_sverena_vec`) a pak se převzetí označí — `rucne_oznaceno`.
 */
export interface NaPapireVstup {
  priloha_id: string;
  poznamka?: string;
}
export const oznacitOoppNaPapire = (id: string, podId: string, telo: NaPapireVstup) =>
  api.post<ZamOopp>(`${cesta(id, 'oopp')}/${podId}/podepsano-na-papire`, telo);
export const oznacitVecNaPapire = (id: string, podId: string, telo: NaPapireVstup) =>
  api.post<ZamSverenaVec>(`${cesta(id, 'sverene-veci')}/${podId}/podepsano-na-papire`, telo);

export type HodnoceniVstup = Schema<'ZamHodnoceniVstup'>;
export const zapsatHodnoceni = (id: string, telo: HodnoceniVstup) =>
  api.post<ZamHodnoceni>(cesta(id, 'hodnoceni'), telo);
export const upravitHodnoceni = (id: string, podId: string, telo: HodnoceniVstup) =>
  api.put<ZamHodnoceni>(`${cesta(id, 'hodnoceni')}/${podId}`, telo);
export const smazatHodnoceni = (id: string, podId: string) =>
  api.delete<void>(`${cesta(id, 'hodnoceni')}/${podId}`);

export type UrazVstup = Schema<'ZamUrazVstup'>;
export const zapsatUraz = (id: string, telo: UrazVstup) =>
  api.post<ZamUraz>(cesta(id, 'urazy'), telo);
export const upravitUraz = (id: string, podId: string, telo: UrazVstup) =>
  api.put<ZamUraz>(`${cesta(id, 'urazy')}/${podId}`, telo);
export const potvrditUraz = (id: string, podId: string, doIsr: boolean) =>
  api.post<ZamUraz>(`${cesta(id, 'urazy')}/${podId}/potvrdit`, { do_isr: doIsr });
export async function nacistKnihuUrazu(
  stav?: ZamUraz['stav'],
  signal?: AbortSignal,
): Promise<Seznam<ZamUraz>> {
  const q = new URLSearchParams({ velikost: '200' });
  if (stav) q.set('stav', stav);
  return api.get<Seznam<ZamUraz>>(`/urazy?${q.toString()}`, { signal });
}
export function useKnihaUrazu(stav: ZamUraz['stav'] | undefined, povoleno: boolean) {
  return useQuery({
    queryKey: ['urazy', stav ?? 'vse'],
    queryFn: ({ signal }) => nacistKnihuUrazu(stav, signal),
    enabled: povoleno,
  });
}

export const odskrtnoutPolozku = (
  id: string,
  telo: { typ: 'onboarding' | 'offboarding'; polozka_kod: string; hotovo: boolean },
) => api.put<ZamChecklistPrehled>(cesta(id, 'checklist'), telo);

export type BenefitVstup = Schema<'ZamBenefitVstup'>;
export const ulozitBenefity = (id: string, telo: BenefitVstup) =>
  api.put<ZamBenefit>(cesta(id, 'benefity'), telo);

/** Dnešek RRRR-MM-DD v Praze (výchozí datum v polích; zapisuje ho server, tohle je jen předvyplnění). */
export function dnesIso(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Prague' }).format(new Date());
}
